import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";

/**
 * LockApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/lockApi/lockApi.service.js.
 *
 * Acquires / releases pessimistic locks per resource type. `force: true`
 * appends `/force` to the URL, which lets an authorized user override an
 * existing lock.
 *
 * `createMany` / `removeMany` issue one request per id in parallel via
 * `Promise.all` (1:1 with the legacy).
 */
const LOCKS_BASE_V2 = "/v2/api/locks/lock";

interface LockProps {
  type: string;
  id: number | string;
  force?: boolean;
}

interface LockBulkProps {
  type: string;
  ids: Array<number | string>;
}

@Injectable({ providedIn: "root" })
export class LockApiService {
  private readonly http = inject(HttpClient);

  create(props: LockProps): Promise<AjsResponse<unknown>> {
    const suffix = props.force ? "/force" : "";
    return wrapAjs(
      this.http.post<unknown>(
        `${LOCKS_BASE_V2}/${props.type}/${props.id}${suffix}`,
        {},
      ),
    );
  }

  remove(props: LockProps): Promise<AjsResponse<unknown>> {
    const suffix = props.force ? "/force" : "";
    return wrapAjs(
      this.http.delete<unknown>(
        `${LOCKS_BASE_V2}/${props.type}/${props.id}${suffix}`,
      ),
    );
  }

  createMany(props: LockBulkProps): Promise<AjsResponse<unknown>[]> {
    return Promise.all(
      props.ids.map((id) => this.create({ type: props.type, id })),
    );
  }

  removeMany(props: LockBulkProps): Promise<AjsResponse<unknown>[]> {
    return Promise.all(
      props.ids.map((id) => this.remove({ type: props.type, id })),
    );
  }
}
